import Bugsnag from '@bugsnag/js'
import { PaymentRequestButtonElement, useStripe } from '@stripe/react-stripe-js'
import {
  PaymentRequest,
  PaymentRequestOptions,
  PaymentRequestShippingAddress,
  PaymentRequestShippingAddressEvent,
  PaymentRequestShippingOption,
  PaymentRequestShippingOptionEvent,
  PaymentRequestTokenEvent,
  PaymentRequestUpdateDetails,
} from '@stripe/stripe-js'
import omitBy from 'lodash/omitBy'
import { useRouter } from 'next/router'
import { Dispatch, useEffect, useMemo, useRef } from 'react'

import { useCreateAddressMutation } from 'api/addressesApi'
import { useCreateBillingInfoMutation } from 'api/billingInfosApi'
import {
  useBuyOrderMutation,
  useCreateOrderMutation,
  useLazyGetPriceEstimateQuery,
  useUpdateOrderMutation,
} from 'api/ordersApi'
import { useCreateGuestMutation } from 'api/usersApi'
import { IAction, IApplePayEnabled, IErrorAction } from 'checkout/containers/CheckoutStateReducer'
import { createOrderBodyPayload } from 'checkout/utils/createOrderBodyPayload'
import { isTimeoutError, timeoutMessage } from 'checkout/utils/timeout'
import { t } from 'localization'
import ShippingAddressType from 'server/enums/ShippingAddressType'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'
import PaymentType from 'shared/enums/PaymentType'
import { PriceBreakdownLineItemType } from 'shared/enums/PriceBreakdownLineItemType'
import { useAuth } from 'shared/hooks/useAuth'
import { useUser } from 'shared/hooks/useUser'
import { Address } from 'shared/types/Address'
import { Order, PartialOrder } from 'shared/types/Order'
import { selectShippingAddress } from 'store/checkoutSlice'
import { useAppSelector } from 'store/hooks'

const TP = 'checkout.hooks.useExpressPayRTK'

type ExpressPayOrderInput = {
  billingAddress: Partial<Address>
  billingInfo: {
    name?: string
    orderNumber?: number
    stripeToken: string
  }
  email?: string
  finalPriceCents: number
  isInstantShip?: boolean
  orderNumber?: number
  product?: {
    id?: number
    productTemplate?: { id?: number }
    size?: { value?: number }
  }
  shippingAddress: Partial<Address>
  shippingServiceLevelId: string
}

type GetStripeBuyData = {
  _order?: PartialOrder
  event: PaymentRequestTokenEvent
}

type GetStripePaymentRequestOptions = {
  _order?: PartialOrder
  _currencyCode?: string
}

type PaymentRequestUpdateEvents =
  | PaymentRequestShippingAddressEvent
  | PaymentRequestShippingOptionEvent

type UseExpressPayProps = {
  dispatch: Dispatch<IAction | IErrorAction | IApplePayEnabled>
  displayCheckoutRTK: boolean
  order?: Order
}

// Express Pay aka Apple Pay, Google Pay, Giro Pay (Germany), etc
export const useExpressPayRTK = ({ dispatch, displayCheckoutRTK, order }: UseExpressPayProps) => {
  const router = useRouter()
  const stripe = useStripe()
  const address = useAppSelector(selectShippingAddress)
  const { currentUser, isAuthenticated } = useUser()
  const { logoutUser } = useAuth()
  const { country: countryCode, currencyCode } = useShoppingRegionContext()

  const [buyOrder] = useBuyOrderMutation()
  const [createAddress] = useCreateAddressMutation()
  const [createBillingInfo] = useCreateBillingInfoMutation()
  const [createGuest] = useCreateGuestMutation()
  const [createOrder] = useCreateOrderMutation()
  const [getPriceEstimate] = useLazyGetPriceEstimateQuery()
  const [updateOrder] = useUpdateOrderMutation()

  // ExpressPayButton will not re-render while the sheet is popped
  // so we need a ref for paymentRequest, shippingAddress, shippingOption, order
  const paymentRequest = useRef<PaymentRequest>()
  const shippingAddress = useRef<PaymentRequestShippingAddress>()
  const shippingOption = useRef<PaymentRequestShippingOption>()
  const updatedOrder = useRef<Order>()
  const isAuthenticatedRef = useRef(isAuthenticated)

  const fallbackErrorMessage = t(
    `${TP}.requestFailure`,
    'Something went wrong.  Please try again or reach out to support@flightclub.com',
  )

  const trackError = (methodName: string, error: Error) => {
    const notifyError = new Error(error?.data?.messages?.[0] || error?.message || methodName)
    Bugsnag.notify(notifyError, (event) => {
      event.addMetadata('checkout', {
        addressId: order?.addressId,
        billingInfoId: order?.billingInfoId,
        id: order?.id,
        isInstantShip: order?.isInstantShip,
        localizedCreditCents: order?.localizedCreditCents,
        localizedFinalPriceCents: order?.localizedFinalPriceCents,
        localizedPromoCodeValueCents: order?.localizedPromoCodeValueCents,
        localizedShippingCents: order?.localizedShippingCents,
        orderNumber: order?.number,
        paymentType: order?.paymentType,
        shippingAddressInRegion: order?.shippingAddressInRegion,
        shippingServiceLevel: order?.shippingServiceLevel,
        status: order?.status,
      })
      event.addMetadata('error', {
        dataMessages: error?.data?.messages,
        file: 'useExpressPayRTK',
        message: error?.message,
        method: methodName,
        status: error?.status,
      })
      event.addMetadata('product', {
        boxCondition: order?.product?.boxCondition,
        consigned: order?.product?.consigned,
        id: order?.product?.id,
        name: order?.product?.productTemplate?.name,
        saleStatus: order?.product?.saleStatus,
        shoeCondition: order?.product?.shoeCondition,
        size: order?.product?.size,
        slug: order?.product?.slug || order?.product?.productTemplate?.slug,
        variantShoeCondition: order?.product?.variantShoeCondition,
      })
      event.addMetadata('user', {
        id: currentUser?.id,
        isGuest: currentUser?.isGuest,
      })
      event.context = 'checkout'
      event.severity = 'error'
    })
  }

  const buyWithExpressPay = async (input: ExpressPayOrderInput) => {
    const {
      billingAddress,
      billingInfo,
      finalPriceCents,
      shippingAddress: _shippingAddress,
      shippingServiceLevelId,
      email,
      product,
    } = input

    // Get order number if available in input; if unavailable, create guest and guest order
    const orderNumber = await (async () => {
      if (input?.orderNumber) {
        return input?.orderNumber
      }

      // Create guest user
      const createGuestBody = { guestEmail: email }
      await createGuest(createGuestBody).unwrap()

      // Create guest order
      const createOrderBody = createOrderBodyPayload({
        boxCondition: router?.query?.boxCondition,
        condition: router?.query?.condition,
        expectedPriceCents: finalPriceCents || null,
        instantShip: router?.query?.instantShip,
        productId: product?.id || null,
        productTemplateId: product?.productTemplate?.id || null,
        size: product?.size?.value,
      })
      const guestOrder = await createOrder(createOrderBody).unwrap()

      return guestOrder.number
    })()

    if (!orderNumber) {
      const error = new Error(
        t(
          `${TP}.unableToCreateOrder`,
          'Unable to create order for express pay, please try another payment. (Missing order number)',
        ),
      )

      trackError('buyWithExpressPay', error)
      throw error
    }

    // Create shipping address
    const shippingAddressBody = {
      address: omitBy(
        {
          ..._shippingAddress,
          addressType: ShippingAddressType.Shipping,
        },
        (x) => x === '' || x === undefined || x === null,
      ),
    }
    const createdShippingAddress = await createAddress(shippingAddressBody).unwrap()

    // Create billing address
    const billingAddressBody = {
      address: omitBy(
        {
          ...billingAddress,
          addressType: ShippingAddressType.Billing,
        },
        (x) => x === '' || x === undefined || x === null,
      ),
    }
    const createdBillingAddress = await createAddress(billingAddressBody).unwrap()

    // Create billing info
    const billingInfoBody = {
      billingInfo: {
        billingAddressId: createdBillingAddress.id,
        name: billingInfo?.name,
        paymentType: PaymentType.ApplePay,
        stripeToken: billingInfo?.stripeToken,
      },
    }
    const createdBillingInfo = await createBillingInfo(billingInfoBody).unwrap()

    // Update order with billing info, shipping address, and shipping service level
    const updateOrderBody = {
      orderNumber,
      order: {
        addressId: createdShippingAddress?.id,
        billingInfoId: createdBillingInfo.id,
        shippingServiceLevel: shippingServiceLevelId,
      },
    }
    await updateOrder(updateOrderBody).unwrap()

    // Purchase order
    const buyOrderBody = {
      finalPriceCents,
      orderNumber,
    }
    return await buyOrder(buyOrderBody).unwrap()
  }

  const createStripeSessionAndCheckCapability = async () => {
    const paymentRequestOptions = getStripePaymentRequestOptions({
      _order: order,
      _currencyCode: currencyCode,
    })
    paymentRequest.current = stripe?.paymentRequest(paymentRequestOptions)
    paymentRequest.current?.on('shippingaddresschange', handleShippingUpdate)
    paymentRequest.current?.on('shippingoptionchange', handleShippingUpdate)
    paymentRequest.current?.on('token', handlePaymentToken)
    const canMakePayment = await paymentRequest.current?.canMakePayment()
    dispatch({
      type: 'applePayAvailable',
      payload: !!canMakePayment,
    })
  }

  /**
   * Sneakers returns localized currencies in their lowest form however stripe
   * expects certain countries to display currencies not in their lowest form.
   * This format is currently only necessary for ApplePay
   * https://stripe.com/docs/currencies#zero-decimal
   */
  const formatStripeCurrencyAmount = (value) => {
    if (Number.isNaN(value)) return
    const STRIPE_ZERO_DECIMAL_CURRENCIES = ['JPY', 'KRW']
    return STRIPE_ZERO_DECIMAL_CURRENCIES.includes(currencyCode) ? Number(value / 100) : value
  }

  const getStripeBuyData = ({ _order, event }: GetStripeBuyData): ExpressPayOrderInput => {
    const {
      payerEmail,
      payerName,
      payerPhone,
      shippingAddress: _shippingAddress,
      shippingOption: _shippingOption,
      token,
    } = event
    const orderNumber = _order?.number

    const orderIdentifier: number | string = orderNumber || 'guest express checkout'

    if (
      !Number.isFinite(_order?.localizedFinalPriceCents?.amount) ||
      !Number.isFinite(_order?.localizedFinalPriceCents?.amountUsdCents)
    ) {
      const error = new Error(
        t(
          `${TP}.issueExpressPay`,
          'There was an issue checking out with express pay, please choose a different payment method.',
        ),
      )

      trackError('getStripeBuyData', error)
      throw error
    }

    if (!shippingAddress) {
      const error = new Error(
        t(`${TP}.noShippingAddress`, {
          defaultValue:
            'No shipping address provided, cannot create stripe payment for {orderIdentifier}',
          orderIdentifier,
        }),
      )

      trackError('getStripeBuyData', error)
      throw error
    }

    if (!_shippingOption?.id) {
      const error = new Error(
        t(`${TP}.noShippingOption`, {
          defaultValue:
            'No shipping option provided, cannot create stripe payment for {orderIdentifier}',
          orderIdentifier,
        }),
      )

      trackError('getStripeBuyData', error)
      throw error
    }

    if (!token?.id) {
      const error = new Error(
        t(`${TP}.noStripeToken`, {
          defaultValue: 'No stripe token, cannot create stripe payment for {orderIdentifier}',
          orderIdentifier,
        }),
      )

      trackError('getStripeBuyData', error)
      throw error
    }

    if (window.sessionStorage.getItem('apple-pay-token') === token.id) {
      const error = new Error(t(`${TP}.tokenUsed`, 'Apple Pay token already used.'))

      trackError('getStripeBuyData', error)
      throw error
    }

    window.sessionStorage.setItem('apple-pay-token', token.id)

    const billingAddress: Partial<Address> = {
      name: payerName || '',
      address1: token.card?.address_line1 || '',
      address2: token.card?.address_line2 || '',
      city: token.card?.address_city || '',
      countryCode: token.card?.address_country || '',
      postalCode: token.card?.address_zip || '',
      phone: payerPhone || '',
      state: token.card?.address_state || '',
    }

    const stripeShippingAddress: Partial<Address> = {
      name: payerName || _shippingAddress?.recipient || '',
      address1: _shippingAddress?.addressLine?.shift() || token.card?.address_line1 || '',
      address2: _shippingAddress?.addressLine?.shift() || token.card?.address_line2 || '',
      city: _shippingAddress?.city || token.card?.address_city || '',
      countryCode: _shippingAddress?.country || token.card?.address_country || '',
      postalCode: _shippingAddress?.postalCode || token.card?.address_zip || '',
      phone: payerPhone || _shippingAddress?.phone || '',
      state: _shippingAddress?.region || token.card?.address_state || '',
    }

    const product = { ..._order?.product }

    return {
      billingAddress,
      billingInfo: {
        name: payerName,
        orderNumber,
        stripeToken: String(token.id),
      },
      email: payerEmail,
      // NOTE: Regardless of currency, backend expects finalPriceCents in USD when completing purchase
      finalPriceCents: _order?.localizedFinalPriceCents?.amountUsdCents,
      isInstantShip: _order?.isInstantShip,
      orderNumber,
      product: {
        id: Number(product?.id),
        productTemplate: { id: Number(product?.productTemplate?.id) },
        size: { value: Number(product?.size) },
      },
      shippingAddress: stripeShippingAddress,
      shippingServiceLevelId: _shippingOption?.id,
    }
  }

  const getStripeCheckoutDisplayItems = (
    _order: PartialOrder,
  ): PaymentRequestOptions['displayItems'] => {
    const displayItems = _order?.pricingBreakdown?.lineItems
      ?.filter((lineItem) => {
        if (lineItem?.type !== PriceBreakdownLineItemType.INCLUDED_TAX_AND_DUTY) {
          return lineItem
        }
      })
      .map((lineItem) => {
        return {
          amount: formatStripeCurrencyAmount(lineItem?.localizedAmount?.amount),
          label: lineItem?.label,
        }
      })
    return displayItems
  }

  const getStripeDisplayOptions = (_order?: PartialOrder): PaymentRequestUpdateDetails => {
    const finalPrice = Number(_order?.localizedFinalPriceCents?.amount)

    if (!_order || Number.isNaN(finalPrice)) {
      const error = new Error(t(`${TP}.tokenUsed`, 'Apple Pay token already used.'))

      trackError('getStripeDisplayOptions', error)
      throw error
    }

    return {
      displayItems: getStripeCheckoutDisplayItems(_order),
      shippingOptions: getStripeShippingOptions(_order),
      total: {
        label: t(`${TP}.flightClub`, 'Flight Club'),
        amount: formatStripeCurrencyAmount(finalPrice),
      },
    }
  }

  const getStripePaymentRequestOptions = ({
    _order,
    _currencyCode = 'usd',
  }: GetStripePaymentRequestOptions): PaymentRequestOptions => {
    const finalPrice = Number(_order?.localizedFinalPriceCents?.amount)

    // Stripe is initialized at the checkout landing page. For guest checkout, there is
    // no final price and throws an error. Ignore this error and only send to bugsnag if
    // it is an authenticated user with no final price
    if (Number.isNaN(finalPrice) && isAuthenticated) {
      const error = new Error(t(`${TP}.noFinalPrice`, 'Invalid, no final price'))

      // Only send error to bugsnag when order is created but no order number exists
      if (Boolean(_order?.number) || Boolean(router?.query?.orderNumber)) {
        trackError('getStripePaymentRequestOptions', error)
      }

      throw error
    }

    if (!_order) {
      const error = new Error('getStripePaymentRequestOptions must have an order object')

      trackError('getStripePaymentRequestOptions', error)
      throw error
    }

    // UK needs to be passed as GB for Stripe to work properly for UK users
    const country =
      address?.countryCode === 'UK' || countryCode === 'UK'
        ? 'GB'
        : address?.countryCode || countryCode || 'US'

    return {
      country,
      currency: _currencyCode.toLowerCase(),
      displayItems: getStripeCheckoutDisplayItems(_order),
      requestPayerEmail: true,
      requestPayerName: true,
      requestPayerPhone: true,
      requestShipping: true,
      total: {
        label: t(`${TP}.flightClub`, 'Flight Club'),
        amount: formatStripeCurrencyAmount(finalPrice),
      },
    }
  }

  const getStripeShippingOptions = ({
    shippingServiceLevelOptions = [],
    shippingServiceLevel,
  }: PartialOrder) => {
    return shippingServiceLevelOptions
      .filter((option) => option.available)
      .map((option) => ({
        amount: formatStripeCurrencyAmount(option?.localizedPriceCents?.amount),
        detail: option.description,
        id: option.id,
        label: option.name,
      }))
      .sort((optionA, optionB) => {
        // Apple pay displays the first option as selected
        // so we sort our selected option to first
        if (optionA.id === shippingServiceLevel) return -1
        if (optionB.id === shippingServiceLevel) return 1
        return 0
      })
  }

  // paymentTokens are generated after a user authorizes an express pay transaction.
  // Sneakers uses the token to complete payment via stripe
  const handlePaymentToken = async (event: PaymentRequestTokenEvent) => {
    try {
      dispatch({ type: 'expressCheckoutRequest' })

      // For guest express checkout updated order is not complete therefore must
      // populate missing information from query params passed from PDP
      if (!isAuthenticated) {
        updatedOrder.current = {
          ...updatedOrder.current,
          // NOTE: Regardless of currency, backend expects finalPriceCents in USD when completing purchase
          finalPriceCents: updatedOrder.current?.localizedFinalPriceCents?.amountUsdCents,
          isInstantShip: router?.query?.instantShip === 'true',
          product: {
            id: Number(router?.query?.productId) || null,
            productTemplate: {
              id: Number(router?.query?.productTemplateId) || null,
            },
            size: Number(router?.query?.size) || null,
          },
        }
      }

      const expressPayOrder = getStripeBuyData({ _order: updatedOrder.current, event })
      const purchasedOrder = await buyWithExpressPay(expressPayOrder)
      event.complete('success')
      router.push(
        `/checkout/${purchasedOrder?.number}/success`,
        `/checkout/${purchasedOrder?.number}/success`,
        {
          locale: router.locale,
        },
      )
    } catch (error) {
      const errorMessage = error?.data?.localized_messages?.[0] || error?.data?.messages?.[0]
      if (isTimeoutError(error)) {
        dispatch({ type: 'orderRequestFailure', payload: timeoutMessage() })
        await logoutUser()
        return
      }
      if (currentUser?.isGuest) {
        await logoutUser()
      }
      trackError('handlePaymentToken', error)
      dispatch({
        type: 'expressCheckoutFailure',
        payload: errorMessage || error?.message || fallbackErrorMessage,
      })
      event.complete('fail')
    }
  }

  // We do not send stripe any address info, all address info comes from what the user has saved for
  // express pay, initially our order pricing is based off our stored address. Upon opening of express
  // pay window, shippingaddresschange event is sent and we can update order based on express payment
  // address info (which is only a partial address so we update on an order esitmate, but do not
  // update the actual order until checkout)
  const handleShippingUpdate = async (event: PaymentRequestUpdateEvents) => {
    if ('shippingAddress' in event) {
      shippingAddress.current = event.shippingAddress
    }
    if ('shippingOption' in event) {
      shippingOption.current = event.shippingOption
    }
    try {
      // Get price estimate as partial order object
      const priceEstimateBody = omitBy(
        {
          'address[city]': shippingAddress.current?.city,
          'address[countryCode]': shippingAddress.current?.country,
          'address[postalCode]': shippingAddress.current?.postalCode,
          'address[state]': shippingAddress.current?.region,
          'order[instantShip]':
            updatedOrder.current?.isInstantShip || router?.query?.instantShip === 'true',
          'order[productId]':
            updatedOrder.current?.product?.id || Number(router?.query?.productId) || null,
          'order[productTemplateId]':
            updatedOrder.current?.product?.productTemplate?.id ||
            Number(router?.query?.productTemplateId) ||
            null,
          'order[shippingServiceLevel]':
            shippingOption.current?.id || updatedOrder.current?.shippingServiceLevel,
          'order[size]':
            updatedOrder.current?.product?.sizeOption?.value || Number(router?.query?.size) || null,
          isAuthenticated: isAuthenticatedRef.current,
          orderNumber: updatedOrder.current?.number,
        },
        (x) => x === '' || x === null || x === undefined,
      )
      const priceEstimateOrder = await getPriceEstimate(priceEstimateBody).unwrap()

      updatedOrder.current = Object.assign({}, updatedOrder.current, priceEstimateOrder)
      const displayOptions = getStripeDisplayOptions(updatedOrder.current)
      displayOptions.status = 'success'
      event.updateWith(displayOptions)
    } catch (error) {
      const errorMessage = error?.data?.localized_messages?.[0] || error?.data?.messages?.[0]
      if (isTimeoutError(error)) {
        dispatch({ type: 'orderRequestFailure', payload: timeoutMessage() })
        await logoutUser()
        return
      }
      trackError('handleShippingUpdate', error)
      dispatch({
        type: 'expressCheckoutFailure',
        payload: errorMessage || error?.message || fallbackErrorMessage,
      })
      event.updateWith({ status: 'fail' })
    }
  }

  useEffect(() => {
    if (displayCheckoutRTK && stripe && !!order) {
      updatedOrder.current = order
      createStripeSessionAndCheckCapability()
    }
  }, [displayCheckoutRTK, stripe, order])

  // Update the displayItems object if promotion id changes to capture
  // new promotion price
  useEffect(() => {
    if (stripe) {
      updatedOrder.current = order
      paymentRequest.current?.update(getStripeDisplayOptions(order))
    }
  }, [stripe, order?.promotion?.id, !!paymentRequest.current])

  // in handleShippingUpdate the value of isAuthenticatedRef.current is used
  // to avoid stale closures and get the correct price breakdown for the order
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated
  }, [isAuthenticated])

  // useMemo stops the paymentRequestButton from re-rendering / flickering
  const ExpressPayButton = useMemo(() => {
    if (paymentRequest.current) {
      return function ExpressPayButtonElement() {
        return <PaymentRequestButtonElement options={{ paymentRequest: paymentRequest.current! }} />
      }
    }
    return () => null
  }, [!!paymentRequest.current])

  return { ExpressPayButton }
}
